import _ from "lodash";
import React, { useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import classNames from "classnames";
import classes from "./MarkdownView.scss";
import "katex/dist/katex.min.css";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  c,
  cpp,
  java,
  javascript,
  css,
  python,
  json,
  sql,
  markdown,
  xml,
  htmlbars,
  ruby,
  swift,
  r,
  go,
  kotlin,
  lua,
  php,
  yaml,
} from "react-syntax-highlighter/dist/esm/languages/hljs";
import { vs } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { colors } from "Constants";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import { CopyOutlined } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";
import { copyToClipboard } from "Utils/CopyToClipboard";
import { setToastMsg } from "routes/modules/LoginModule";
import rehypeRaw from "rehype-raw";
import { sanitizeLatexText } from "@toddle-edu/common-utils";

const LIGHT_THEME_STYLES = {
  backgroundColor: "#F5F5F5",
  border: "1px solid #CCCCCC",
};

const getStyles = isDarkTheme => {
  const codeBlockCustomStyles = {
    borderRadius: "8px",
    fontSize: "1.2rem",
    lineHeight: "1.8rem",
    ...(isDarkTheme ? {} : LIGHT_THEME_STYLES),
  };

  const codeBlocklineNumberStyles = {
    color: colors.textLink,
  };

  const inlineCodeBlockCustomStyles = {
    borderRadius: "8px",
    display: "inline",
    padding: "0.25em",
    ...(isDarkTheme ? {} : { backgroundColor: colors.surfaceSubtleHover }),
  };

  return {
    codeBlockCustomStyles,
    codeBlocklineNumberStyles,
    inlineCodeBlockCustomStyles,
  };
};

const SUPPORTED_LANGUAGES = {
  c: c,
  cpp: cpp,
  java: java,
  javascript: javascript,
  css: css,
  python: python,
  json: json,
  sql: sql,
  markdown: markdown,
  xml: xml,
  html: htmlbars,
  ruby: ruby,
  swift: swift,
  r: r,
  go: go,
  kotlin: kotlin,
  lua: lua,
  php: php,
  yaml: yaml,
};

_.forEach(SUPPORTED_LANGUAGES, (lang, key) => {
  SyntaxHighlighter.registerLanguage(key, lang);
});

const CodeBlock = props => {
  const { children, codeThemeConfig } = props;

  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();

  const onCopyClick = e => {
    e.stopPropagation();
    // copy code to clipboard
    copyToClipboard(children, () => {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:copied_capital",
          type: "success",
        })
      );
    });
  };

  const { theme, isDarkTheme, shouldRemoveSyntaxHighlighterStyle } =
    codeThemeConfig || {};

  const styles = getStyles(isDarkTheme);

  const highlighterStyle = shouldRemoveSyntaxHighlighterStyle
    ? undefined
    : theme;

  const customStyle = props.inline
    ? styles.inlineCodeBlockCustomStyles
    : styles.codeBlockCustomStyles;

  return (
    <div
      className={classes.codeBlockContainer}
      style={{ display: props.inline ? "inline" : "block" }}
    >
      <SyntaxHighlighter
        {...props}
        style={highlighterStyle}
        // eslint-disable-next-line react/no-children-prop
        children={String(children).replace(/\n$/, "")}
        showLineNumbers={!props.inline}
        lineNumberStyle={styles.codeBlocklineNumberStyles}
        customStyle={customStyle}
        PreTag={props.inline ? "span" : "div"}
        wrapLongLines={props.inline}
      />
      {!props.inline ? (
        <div className={classes.copyButtonContainer}>
          <Tooltip tooltip={t("common:copy")} placement="top">
            <IconButton
              _legacy_variant="plain"
              variant="neutral"
              type="plain"
              _legacy_size="x-small"
              size="small"
              onClick={onCopyClick}
              icon={<CopyOutlined variant="subtle" />}
            />
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
};

const HIGHLIGHTED_TEXT_OPENNING_TAG = "<annt";
const HIGHLIGHTED_TEXT_CLOSING_TAG = "</annt>";

const processContentForHighlightedText = ({
  content,
  highlightedTextEndIndexRef,
}) => {
  const openingTagIndex = content.indexOf(
    HIGHLIGHTED_TEXT_OPENNING_TAG,
    Math.max(0, highlightedTextEndIndexRef.current)
  );
  const closingTagIndex = content.indexOf(
    HIGHLIGHTED_TEXT_CLOSING_TAG,
    Math.max(0, openingTagIndex)
  );

  if (openingTagIndex > closingTagIndex) {
    // find if opening tag is finished with ">"
    const openingTagEndIndex = content.lastIndexOf(">");

    if (openingTagEndIndex === -1 || openingTagEndIndex < openingTagIndex) {
      return content.slice(0, openingTagIndex);
    }

    const beforeTagContent = content.slice(0, openingTagIndex);
    const afterTagContent = content.slice(openingTagEndIndex + 1);

    return `${beforeTagContent}${afterTagContent}`;
  }

  highlightedTextEndIndexRef.current =
    closingTagIndex + HIGHLIGHTED_TEXT_CLOSING_TAG.length;

  return content;
};

const MarkdownView = React.memo(props => {
  const {
    content,
    components,
    containerStyle = {},
    contentContainerClass,
    enableHTMLrendering = false,
    enableHighlightedText = false,
    enableLatexSanitization = false,
    codeThemeConfig = {
      theme: vs,
      isDarkTheme: false,
      shouldRemoveSyntaxHighlighterStyle: false,
    },
  } = props;

  const internalComponents = {
    code: codeProps => (
      <CodeBlock {...codeProps} codeThemeConfig={codeThemeConfig} />
    ),
  };

  let contentValue = content;
  const highlightedTextEndIndexRef = useRef(-1);

  // KaTeX does not support \differentialD, so it is parsed as text instead of a math identifier ,
  // causing misrendering of derivative expressions. Mapping \differentialD to \mathrm{d} via a KaTeX macro
  // ensures correct rendering without changing the mathematical meaning.
  const rehypePlugins = [
    [
      rehypeKatex,
      {
        macros: {
          "\\differentialD": "\\mathrm{d}",
        },
      },
    ],
  ];

  if (enableHTMLrendering) {
    rehypePlugins.push(rehypeRaw);
  }

  if (enableHighlightedText) {
    contentValue = processContentForHighlightedText({
      content,
      highlightedTextEndIndexRef,
    });
  }

  if (enableLatexSanitization) {
    contentValue = sanitizeLatexText(contentValue);
  }

  const contentContainer = classNames({
    [contentContainerClass]: !_.isEmpty(contentContainerClass),
  });

  return (
    <div className={classes.container} style={containerStyle}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={rehypePlugins}
        className={contentContainer}
        components={_.merge(internalComponents, components)}
      >
        {contentValue}
      </ReactMarkdown>
    </div>
  );
});

MarkdownView.displayName = "MarkdownView";
export default MarkdownView;
